import React, { useContext, useState } from "react"
import moment from "moment"

import { funEU } from "../../../utils/firebase"
import { ModalContext } from "../../../utils/providers/modal"
import Badge from "../../../components/ui/badge/badge"
import Button from "../../../components/ui/button/button"

import { runClassifierWithDefaults } from "./ai-tools/run-classifier"
import ClassificationBadge from "./classification-badge"

/**
 * Single `<tr>` rendered inside the per-mailbox inbox `Table`. Click anywhere
 * on the row (other than the action buttons) opens the email detail drawer.
 *
 * Action cell contains two buttons:
 *
	 *   - Classify — runs the classifier with the canonical registry defaults
 *     and persists the result back to the doc. Snapshot listener refreshes
 *     the row's Classification + Confidence cells when the write completes.
 *   - Delete — irreversible, with confirmation modal.
 */
function EmailRow({ comm, onSelect }) {
	const { showModal } = useContext(ModalContext)
	const [busy, setBusy] = useState(null) // "delete" | "reclassify" | null

	const occurredAt = comm.occurred_at?.seconds
		? moment.unix(comm.occurred_at.seconds).format("MMM D[,] YYYY HH:mm")
		: "—"

	const fromLabel = comm.from?.name
		? `${comm.from.name} <${comm.from.address}>`
		: comm.from?.address || "—"

	const performDelete = async () => {
		setBusy("delete")
		try {
			const callable = funEU.httpsCallable("deleteCommunication")
			await callable({ comm_id: comm.id })
			// Snapshot listener removes the row.
		} catch (err) {
			showModal({
				type: "ALERT",
				title: "Delete failed",
				body: err?.message || "Failed to delete communication",
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
			setBusy(null)
		}
	}

	const performReclassify = async (e) => {
		e.stopPropagation()
		setBusy("reclassify")
		try {
			await runClassifierWithDefaults(comm.id)
			// Snapshot listener picks up the updated classification + confidence.
		} catch (err) {
			showModal({
				type: "ALERT",
				title: "Classification failed",
				body: err?.message || "Failed to run classifier",
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
		} finally {
			setBusy(null)
		}
	}

	const confirmDelete = (e) => {
		// Stop the click bubbling up to the row's onClick which would otherwise
		// open the drawer behind the confirmation modal.
		e.stopPropagation()

		showModal({
			type: "ALERT",
			title: "Delete this email?",
			body:
				`This permanently removes the Firestore document, the body file in Cloud Storage, ` +
				`and any attachments associated with this email. If the email also arrived in other ` +
				`monitored mailboxes (de-duplicated), it disappears from those too. This can't be undone.`,
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, delete", action: () => performDelete() },
		})
	}

	return (
		<tr
			data-system-id={comm.id}
			className="email-row"
			onClick={() => onSelect(comm)}>
			<td>{occurredAt}</td>
			<td className="email-row-from">{fromLabel}</td>
			<td className="email-row-subject">
				{comm.subject || <small className="no-value">(no subject)</small>}
			</td>
			<td>
				<ClassificationBadge comm={comm} />
				{comm.tier === "RESTRICTED" && (
					<Badge
						type="NEGATIVE"
						label="Restricted"
					/>
				)}
			</td>
			<td>
				<ConfidenceCell confidence={comm.classification_confidence} />
			</td>
			<td>
				<AttachmentSummary comm={comm} />
			</td>
			{/* Action cell mirrors the bookings-received "View booking + View in
			    new tab" pattern: buttons sit directly inside `is-button`, no
			    wrapper div. The `mailbox-emails-table` SCSS overrides Button to
			    `display: inline-flex; width: fit-content` so they flow inline
			    instead of stacking. */}
			<td className="is-button">
				<Button
					small={true}
					label="Classify"
					loading={busy === "reclassify"}
					loadingText="Classifying..."
					onClick={performReclassify}
				/>
				<Button
					small={true}
					label="Delete"
					loading={busy === "delete"}
					loadingText="Deleting..."
					onClick={confirmDelete}
				/>
			</td>
		</tr>
	)
}

/**
 * Renders the model's confidence as a percentage. Treats unset/null/NaN as
 * "no classification yet" and shows an em-dash.
 */
function ConfidenceCell({ confidence }) {
	if (typeof confidence !== "number" || !isFinite(confidence)) {
		return <small className="no-value">—</small>
	}
	const pct = Math.round(confidence * 100)
	return <span className="email-row-confidence">{pct}%</span>
}

function AttachmentSummary({ comm }) {
	const attachments = (comm.attachments || []).filter((attachment) => !attachment.is_inline)
	const imageCount = attachments.filter((attachment) =>
		attachment.content_type?.startsWith("image/")
	).length
	const fileCount = attachments.length - imageCount

	if (attachments.length === 0) {
		return <small className="no-value">—</small>
	}

	const parts = []
	if (fileCount > 0) {
		parts.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`)
	}
	if (imageCount > 0) {
		parts.push(`${imageCount} image${imageCount === 1 ? "" : "s"}`)
	}

	return <small className="email-row-attachment-summary">{parts.join(", ")}</small>
}

export default EmailRow
